import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { RTL_LANGUAGES } from '../../i18n';

interface CTABannerProps {
  title: string;
  subtitle?: string;
  buttonText: string;
  to?: string;
}

export default function CTABanner({ title, subtitle, buttonText, to = '/contact' }: CTABannerProps) {
  const { i18n } = useTranslation();
  const isRTL = RTL_LANGUAGES.includes(i18n.language);
  const ArrowIcon = isRTL ? ArrowLeft : ArrowRight;

  return (
    <section className="relative py-24 md:py-32 overflow-hidden">
      {/* Bright gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-cyan-500 via-cyan-600 to-teal-600" />
      {/* Warm soft overlay shapes */}
      <div className="absolute top-0 start-0 w-96 h-96 rounded-full bg-white/10 blur-[100px]" />
      <div className="absolute bottom-0 end-0 w-80 h-80 rounded-full bg-emerald-300/15 blur-[80px]" />
      {/* Dot pattern */}
      <div className="absolute inset-0 opacity-10" style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
        backgroundSize: '32px 32px',
      }} />

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        className="relative max-w-3xl mx-auto px-4 sm:px-6 text-center"
      >
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 tracking-tight">
          {title}
        </h2>
        {subtitle && (
          <p className="text-cyan-50/90 text-lg md:text-xl mb-8 leading-relaxed">
            {subtitle}
          </p>
        )}
        <Link
          to={to}
          className="group inline-flex items-center gap-2.5 px-8 py-4 bg-white text-cyan-700 font-bold rounded-2xl text-base hover:bg-cyan-50 transition-all shadow-xl shadow-cyan-800/15 hover:-translate-y-0.5"
        >
          {buttonText}
          <ArrowIcon className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" />
        </Link>
      </motion.div>
    </section>
  );
}
